import {
  detectCsvDelimiter,
  parseCSVRow,
  parseCsvNumber,
  splitCsvLines,
} from "./csv-utils";

// Utility to convert numbers to words (Indian numbering system)
export function numberToWords(num: number): string {
  if (num === 0) return "Zero";
  
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  
  function convertHundreds(n: number): string {
    let result = "";
    
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    } else if (n >= 10) {
      result += teens[n - 10] + " ";
      return result.trim();
    }
    
    if (n > 0) {
      result += ones[n] + " ";
    }
    
    return result.trim();
  }
  
  if (num >= 10000000) { // 1 Crore
    const crores = Math.floor(num / 10000000);
    const remainder = num % 10000000;
    let result = convertHundreds(crores) + " Cr";
    if (remainder > 0) {
      result += " " + numberToWords(remainder);
    }
    return result;
  }
  
  if (num >= 100000) { // 1 Lakh
    const lakhs = Math.floor(num / 100000);
    const remainder = num % 100000;
    let result = convertHundreds(lakhs) + " Lakh";
    if (remainder > 0) {
      result += " " + numberToWords(remainder);
    }
    return result;
  }
  
  if (num >= 1000) { // 1 Thousand
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    let result = convertHundreds(thousands) + " Thousand";
    if (remainder > 0) {
      result += " " + numberToWords(remainder);
    }
    return result;
  }
  
  return convertHundreds(num);
}

export function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

// Generate sample CSV content for asset upload
export function generateSampleCSV(gstTreatment: "cgst_sgst" | "cgst_utgst" | "igst" = "cgst_sgst"): string {
  const baseHeaders = ["Item Name", "Description", "HSN Code", "Unit Price", "Quantity"];
  const taxHeaders =
    gstTreatment === "igst"
      ? ["IGST %"]
      : gstTreatment === "cgst_utgst"
        ? ["CGST %", "UTGST %"]
        : ["CGST %", "SGST %"];
  const headers = [...baseHeaders, ...taxHeaders];

  const sampleRows =
    gstTreatment === "igst"
      ? [
          ["Laptop Computer", "Business laptop", "8471", "50000", "2", "18"],
          ["Office Chair", "Ergonomic chair", "9401", "15000", "5", "18"],
          ["Printer Toner", "Laser toner cartridge", "8443", "3500", "10", "18"],
        ]
      : gstTreatment === "cgst_utgst"
        ? [
            ["Laptop Computer", "Business laptop", "8471", "50000", "2", "9", "9"],
            ["Office Chair", "Ergonomic chair", "9401", "15000", "5", "9", "9"],
            ["Printer Toner", "Laser toner cartridge", "8443", "3500", "10", "9", "9"],
          ]
        : [
            ["Laptop Computer", "Business laptop", "8471", "50000", "2", "9", "9"],
            ["Office Chair", "Ergonomic chair", "9401", "15000", "5", "9", "9"],
            ["Printer Toner", "Laser toner cartridge", "8443", "3500", "10", "9", "9"],
          ];

  return [headers, ...sampleRows].map((row) => row.join(",")).join("\n");
}

// Parse CSV content to asset lines
export function parseCSVToAssets(csvContent: string) {
  const lines = splitCsvLines(csvContent);
  if (lines.length <= 1) return [];

  const delimiter = detectCsvDelimiter(lines[0]);
  const headers = parseCSVRow(lines[0], delimiter).map((h) =>
    h.toLowerCase().replace(/^\uFEFF/, "").trim()
  );
  const indexOf = (names: string[]) => headers.findIndex((h) => names.includes(h));
  const itemIdx = indexOf(["item name", "item", "item description", "description"]);
  const descriptionIdx = indexOf(["description", "item description"]);
  const hsnIdx = indexOf(["hsn code", "hsn"]);
  const unitPriceIdx = indexOf(["unit price", "unitprice"]);
  const qtyIdx = indexOf(["quantity", "qty"]);
  const cgstIdx = indexOf(["cgst %", "cgst"]);
  const sgstIdx = indexOf(["sgst %", "sgst"]);
  const igstIdx = indexOf(["igst %", "igst"]);
  const utgstIdx = indexOf(["utgst %", "utgst"]);

  return lines.slice(1).flatMap((line) => {
    const values = parseCSVRow(line, delimiter);
    if (values.every((cell) => !cell.trim())) return [];

    const item = itemIdx >= 0 ? values[itemIdx] || "" : "";
    const description = descriptionIdx >= 0 ? values[descriptionIdx] || "" : "";
    const hsn = hsnIdx >= 0 ? values[hsnIdx] || "" : "";
    const unitPrice =
      unitPriceIdx >= 0 ? parseCsvNumber(values[unitPriceIdx] ?? "", 0) : 0;
    const qty = qtyIdx >= 0 ? parseCsvNumber(values[qtyIdx] ?? "", 1) : 1;
    const cgst = cgstIdx >= 0 ? parseCsvNumber(values[cgstIdx] ?? "", 0) : 0;
    const sgst = sgstIdx >= 0 ? parseCsvNumber(values[sgstIdx] ?? "", 0) : 0;
    const igst = igstIdx >= 0 ? parseCsvNumber(values[igstIdx] ?? "", 0) : 0;
    const utgst = utgstIdx >= 0 ? parseCsvNumber(values[utgstIdx] ?? "", 0) : 0;

    return [
      {
        item,
        description,
        hsn,
        unitPrice,
        qty,
        cgst,
        sgst,
        igst,
        utgst,
      },
    ];
  });
}
